import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSession } from '@hooks/security/use-session';
import { useVoucherChoice } from '@hooks/register/screening/use-voucher-choice';
import { useLocalize, type TLocalize } from '@hooks/use-localize';
import { useStepForm } from '@hooks/use-step-form';
import { voucherChoiceSchema } from '@views/register/screening/voucher-choice.schema';
import { DashRegistrationStep, screeningLinkMap } from '@/types/dashboard';
import type { IVoucherChoiceFormValues, IVoucherChoiceModel, IVoucherChoiceSubmitResult } from '@/types/screening/voucher-choice';
import PageLoader from '@components/page-loader';
import ScreeningProgress from '@views/register/screening/screening-progress';
import FieldError from '@components/field-error';
import RequiredFieldsAlert from '@components/required-fields-alert';

// Nkr.Model...Enums.VoucherEducationChoice values.
const OPT_OUT = '0';
const LEARN_MORE = '1';
const OPT_IN = '2';

const VoucherChoice = () => {
    const { registrationId, editKey } = useSession();
    const { model, isLoading, error, submit, isSubmitting, submitError } = useVoucherChoice(registrationId, editKey);

    return (
        <PageLoader isLoading={isLoading} error={error}>
            <VoucherChoiceContent
                model={model}
                isSubmitting={isSubmitting}
                submitError={submitError}
                onSubmit={submit}
            />
        </PageLoader>
    );
};

interface IVoucherChoiceContentProps {
    model: IVoucherChoiceModel;
    isSubmitting: boolean;
    submitError: string | null;
    onSubmit: (values: IVoucherChoiceFormValues) => Promise<IVoucherChoiceSubmitResult>;
}

const asString = (value: number | boolean | null) => (value != null ? String(value) : '');

const VoucherChoiceContent = ({
    model,
    isSubmitting,
    submitError,
    onSubmit,
}: IVoucherChoiceContentProps) => {
    const t = useLocalize(model.languageData);

    const {
        register,
        onSubmit: handleFormSubmit,
        formState: { errors },
    } = useStepForm<IVoucherChoiceFormValues, IVoucherChoiceSubmitResult>({
        resolver: zodResolver(voucherChoiceSchema),
        defaultValues: {
            voucherEducationChoice: asString(model.voucherEducationChoice),
        },
        submit: onSubmit,
        linkMap: screeningLinkMap(model.isFemale, model.isLiver),
    });

    const isRestricted = model.isFromCLM || model.isFromPedGallery;
    const hasIntendedRecipient = model.hasIntendedRecipient === true;

    const optOutLabel = isRestricted
        ? t('DashLabel_ParticipateInTheFV_CLM_No', 'No, I want to donate at this time.')
        : hasIntendedRecipient
            ? t(
                'DashLabel_NoIDoNotWantToParticipateInTheStandardVoucherProgram2',
                'No, I want to donate as a Directed Donor',
            )
            : t('Word_No', 'No');

    const learnMoreLabel = hasIntendedRecipient
        ? t('DashLabel_IWouldLikeToLearnMoreAboutTheStandardVoucherProgram3', "Unsure, I'd like to learn more")
        : t(
            'DashLabel_IWouldLikeToLearnMoreAboutTheFamilyVoucherProgram',
            'I want to learn more about the Family Voucher program before I decide',
        );

    return (
        <div className="row">
            {model.isIE && (
                <div className="alert alert-warning">
                    {t(
                        'Alert_BrowserRecommend',
                        'To ensure proper function of this site, we recommend that you use Chrome or FireFox.',
                    )}
                    .
                </div>
            )}

            <div className="col-xs-12 col-sm-3 hidden-xs">
                <ScreeningProgress model={model} overrideStep={DashRegistrationStep.VoucherChoice} />
            </div>

            <div className="col-xs-12 col-sm-5 summary-mobile-content">
                <RequiredFieldsAlert show={Object.keys(errors).length > 0} t={t} />

                <h1>{t('DashLabel_ProgressVoucherChoice', 'Voucher Choice')}</h1>

                <hr />

                <form className="form-horzontal" onSubmit={handleFormSubmit}>
                    <div className="row row-margined">
                        <div className="col-xs-12">
                            {model.isFromCLM && (
                                <div className="form-group">
                                    <br />
                                    <div className="col-xs-12 col-sm-8 col-md-8 col-sm-offset-1">
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {t(
                                                'DashLabel_ParticipateInTheFV_CLM_1',
                                                'As mentioned in the prior video, your intended recipient may not be deemed eligible for transplant just yet. It can take months or even years for your recipient to need a transplant or be cleared. But you can still help.',
                                            )}
                                        </span>
                                        <br />
                                        <br />
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {t(
                                                'DashLabel_ParticipateInTheFV_CLM_2',
                                                'Participating in the Voucher program will provide your recipient with a better match and a path to a kidney transplant when they are ready.',
                                            )}
                                        </span>
                                        <br />
                                        <br />
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {t(
                                                'DashLabel_ParticipateInTheFV_CLM_3',
                                                'As part of this program potential donors are required to go through the Voucher program initially.',
                                            )}
                                        </span>
                                        <br />
                                        <br />
                                    </div>
                                </div>
                            )}

                            {!model.isFromCLM && model.isFromPedGallery && (
                                <div className="form-group">
                                    <br />
                                    <div className="col-xs-12 col-sm-8 col-md-8 col-sm-offset-1">
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {t(
                                                'DashLabel_ParticipateInTheFV_PedGallery_1',
                                                'As mentioned in the prior video, it is very important to provide your intended pediatric patient with the best match possible.',
                                            )}
                                        </span>
                                        <br />
                                        <br />
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {t(
                                                'DashLabel_ParticipateInTheFV_PedGallery_2',
                                                'Participating in the Voucher Program will provide your recipient with a better match, which can lead to a longer lasting kidney.',
                                            )}
                                        </span>
                                        <br />
                                        <br />
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {t(
                                                'DashLabel_ParticipateInTheFV_PedGallery_3',
                                                'For this group of patients, potential donors are required to go through the Voucher Program initially.',
                                            )}
                                        </span>
                                        <br />
                                        <br />
                                    </div>
                                </div>
                            )}

                            <div className="form-group">
                                <label className="control-label col-xs-12">
                                    {hasIntendedRecipient
                                        ? t(
                                            'DashLabel_ParticipateInTheStandardVoucherProgram',
                                            'Do you want to participate in the Standard Voucher Program',
                                        )
                                        : t(
                                            'DashLabel_ParticipateInTheFamilyVoucherProgram',
                                            'Do you want to participate in the Family Voucher Program',
                                        )}
                                    ?<FieldError message={errors.voucherEducationChoice?.message} />
                                </label>
                                <br />
                                <br />
                                <div
                                    className="col-xs-12 col-sm-10 col-md-10 col-sm-offset-1"
                                    style={{ display: 'grid' }}
                                >
                                    <label>
                                        <input
                                            type="radio"
                                            value={OPT_IN}
                                            {...register('voucherEducationChoice')}
                                        />{' '}
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {hasIntendedRecipient
                                                ? t(
                                                    'DashLabel_YesIWantToParticipateInTheStandardVoucherProgram1',
                                                    'Yes, I want to donate as a Standard Voucher',
                                                )
                                                : t('Word_Yes', 'Yes')}
                                            .
                                        </span>
                                    </label>

                                    <label>
                                        <input
                                            type="radio"
                                            value={OPT_OUT}
                                            {...register('voucherEducationChoice')}
                                        />{' '}
                                        <span style={{ color: 'black', fontWeight: 'normal' }}>
                                            {optOutLabel}.
                                        </span>
                                    </label>

                                    {!isRestricted && (
                                        <label>
                                            <input
                                                type="radio"
                                                value={LEARN_MORE}
                                                {...register('voucherEducationChoice')}
                                            />{' '}
                                            <span style={{ color: 'black', fontWeight: 'normal' }}>
                                                {learnMoreLabel}.
                                            </span>
                                        </label>
                                    )}
                                </div>
                            </div>

                            {submitError && (
                                <div className="form-group col-xs-12">
                                    <div className="alert alert-danger">{submitError}</div>
                                </div>
                            )}

                            <div className="form-group col-xs-12">
                                <button
                                    type="submit"
                                    className="btn btn-primary btn-lg"
                                    style={{ marginTop: 20 }}
                                    disabled={isSubmitting}
                                >
                                    {t('Word_Continue', 'Continue')}
                                </button>
                            </div>
                        </div>
                    </div>
                </form>
            </div>

            <div className="col-xs-12 col-sm-4">
                <VoucherEducationSidebar model={model} t={t} />
            </div>
        </div>
    );
};

interface IVoucherEducationSidebarProps {
    model: IVoucherChoiceModel;
    t: TLocalize;
}

const VoucherEducationSidebar = ({ model, t }: IVoucherEducationSidebarProps) => {
    const [showDetails, setShowDetails] = useState(model.showVem);

    const isStandard = model.hasIntendedRecipient === true;

    const learnMoreTitle = isStandard
        ? t('DashLabel_LearnMoreAboutStandardVoucherDonation', 'Click to Learn More About Standard Voucher Donation')
        : t('DashLabel_LearnMoreAboutFamilyVoucherDonation', 'Click to Learn More About Family Voucher Donation');

    const programName = isStandard
        ? t('DashLabel_TheStandardVoucherProgram', 'Standard Voucher Program')
        : t('DashLabel_TheFamilyVoucherProgram', 'Family Voucher Program');

    const programBenefit = isStandard
        ? t(
            'DashLabel_YouCanHelpYourIntendedRecipientEven',
            'you can help your intended recipient even if you are not a match for them',
        )
        : t(
            'DashLabel_YouCanHelpSomeoneWhoNeeds',
            'you can help someone who needs a kidney now while protecting your family and loved ones if one of them ever needs a kidney in the future',
        );

    return (
        <div style={{ paddingTop: 20 }}>
            {model.voucherVideoUrl !== '' && (
                <div style={{ padding: '56.25% 0 0 0', position: 'relative' }}>
                    <iframe
                        src={model.voucherVideoUrl}
                        allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            border: '0 none',
                        }}
                        title="Voucher Program"
                    ></iframe>
                </div>
            )}

            <div className="panel-group" style={{ marginTop: 20 }}>
                <div className="panel panel-primary">
                    <div className="panel-heading">
                        <h4 className="panel-title">
                            <a
                                role="button"
                                aria-expanded={showDetails}
                                style={{ cursor: 'pointer' }}
                                onClick={() => setShowDetails((prev) => !prev)}
                            >
                                {learnMoreTitle}
                            </a>
                        </h4>
                    </div>

                    {showDetails && (
                        <div className="panel-collapse">
                            <div className="alert alert-info" style={{ color: 'black', marginBottom: 0 }}>
                                <div>
                                    {t('DashLabel_ThroughThe', 'Through the')} <b>{programName}</b>,{' '}
                                    {programBenefit}.
                                </div>

                                <br />

                                <label className="control-label col-xs-12">
                                    <div style={{ display: 'flex' }}>
                                        <div>{t('DashLabel_HowItWorks', 'How it works')}:</div>
                                    </div>
                                </label>

                                <ol>
                                    <li>
                                        {t(
                                            'DashLabel_FVYouNameUpToFiveFamily',
                                            'You name up to five family voucher holders who do not currently need a kidney',
                                        )}
                                        .
                                    </li>
                                    <li>
                                        {t(
                                            'DashLabel_FVEachOfTheFamilyMembersWill',
                                            'Each family voucher holder receives a voucher',
                                        )}
                                        .
                                    </li>
                                    <li>
                                        {t(
                                            'DashLabel_FVYouScheduleYourKidney',
                                            'You schedule your kidney donation surgery when it works best for you',
                                        )}
                                        .
                                    </li>
                                    <li>
                                        {t(
                                            'DashLabel_FVYourKidneyIsMatchedToA',
                                            'Your kidney goes to a patient who is ready for a transplant',
                                        )}
                                        .
                                    </li>
                                    <li>
                                        {t(
                                            'DashLabel_FVIfOneOfYourFamily',
                                            'If one of your family members ever needs a kidney, their transplant center can use their voucher. The NKR will prioritize them for a match',
                                        )}
                                        .
                                    </li>
                                    <li>
                                        {t(
                                            'DashLabel_FVOnceOneFamilyVoucherHolder',
                                            'Only one voucher can be used. Once a voucher is used, the others become void',
                                        )}
                                        .
                                    </li>
                                </ol>

                                <label className="control-label col-xs-12">
                                    <div style={{ display: 'flex' }}>
                                        <div>
                                            {t(
                                                'DashLabel_WhenYouDonateThroughThisProgram',
                                                'When you donate through this program, you also get all the Donor Shield support and protections, including',
                                            )}
                                            :
                                        </div>
                                    </div>
                                </label>

                                <ul>
                                    <li>
                                        {t(
                                            'DashLabel_ReimbursementForLostWages',
                                            'Reimbursement for lost wages, expenses, and dependent care',
                                        )}
                                        .
                                    </li>
                                    <li>
                                        {t(
                                            'DashLabel_PrioritizationForALivingDonor',
                                            'Prioritization for a living donor kidney if you ever need one',
                                        )}
                                        .
                                    </li>
                                    <li>
                                        {t(
                                            'DashLabel_ReimbursementsForComplications',
                                            'Reimbursements for complications costs',
                                        )}
                                        .
                                    </li>
                                    <li>{t('DashLabel_LegalSupport', 'Legal support')}.</li>
                                    <li>
                                        {t(
                                            'DashLabel_RemoteDonationOptions',
                                            'Remote donation options, home blood draws, and more',
                                        )}
                                        .
                                    </li>
                                </ul>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VoucherChoice;
